import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, theme as antTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import 'dayjs/locale/zh-cn';
import dayjs from 'dayjs';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppLayout } from '@/components/Layout';
import { PrivateRoute } from '@/components/PrivateRoute';

import { LoginPage } from '@/pages/login/LoginPage';
import { DashboardOverview } from '@/pages/dashboard/Overview';
import { AppsListPage } from '@/pages/apps/List';
import { AppDetailPage } from '@/pages/apps/Detail';
import { AppWizardPage } from '@/pages/apps/Wizard';
import { WorkflowsListPage } from '@/pages/workflows/List';
import { WorkflowDetailPage } from '@/pages/workflows/Detail';
import { ContentListPage } from '@/pages/content/List';
import { ContentDetailPage } from '@/pages/content/Detail';
import { ContentPlatesPage } from '@/pages/content/Plates';
import { PlatesLibraryPage } from '@/pages/content/PlatesLibrary';
import { ContentBulkImportPage } from '@/pages/content/BulkImport';
import { SuiteWorkflowPage } from '@/pages/suite-workflow/Config';
import { LoRAListPage } from '@/pages/lora/List';
import { BackendsListPage } from '@/pages/backends/List';
import { SKUsListPage } from '@/pages/skus/List';
import { CardKeysListPage } from '@/pages/cardkeys/List';
import { CatalogListPage } from '@/pages/catalog/List';
import { FulfillmentsListPage } from '@/pages/fulfillments/List';
import { OrdersListPage } from '@/pages/orders/List';
import { OrderDetailPage } from '@/pages/orders/Detail';
import { BenefitOrdersPage } from '@/pages/orders/BenefitOrders';
import { TasksListPage } from '@/pages/tasks/List';
import { TaskDetailPage } from '@/pages/tasks/Detail';
import { UsersListPage } from '@/pages/users/List';
import { UserDetailPage } from '@/pages/users/Detail';
import { DiamondsListPage } from '@/pages/diamonds/List';
import { CouponsListPage } from '@/pages/coupons/List';
import { AdAccountsListPage } from '@/pages/ad-accounts/List';
import { AttributionEventsPage } from '@/pages/attribution/Events';
import { IntegrationsPage } from '@/pages/integrations/List';
import { SettingsPage } from '@/pages/settings/CurrentUser';
import { ModerationQueuePage } from '@/pages/moderation/Queue';
import { PaymentChannelsPage } from '@/pages/payments/Channels';
import { MembershipPlansPage } from '@/pages/membership/Plans';
import { MallListPage } from '@/pages/mall/List';
import { MallDetailPage } from '@/pages/mall/Detail';

dayjs.locale('zh-cn');

export function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: antTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#4f46e5',
          borderRadius: 6,
        },
      }}
    >
      <AntApp>
        <ErrorBoundary>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <PrivateRoute>
                    <AppLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardOverview />} />

                <Route path="/apps" element={<AppsListPage />} />
                <Route path="/apps/new" element={<AppWizardPage />} />
                <Route path="/apps/:id" element={<AppDetailPage />} />

                <Route path="/workflows" element={<WorkflowsListPage />} />
                <Route path="/workflows/:code" element={<WorkflowDetailPage />} />

                <Route path="/content" element={<ContentListPage />} />
                <Route path="/content/bulk-import" element={<ContentBulkImportPage />} />
                <Route path="/content/:id" element={<ContentDetailPage />} />
                <Route path="/content/:id/plates" element={<ContentPlatesPage />} />
                <Route path="/plates-library" element={<PlatesLibraryPage />} />
                <Route path="/suite-workflow" element={<SuiteWorkflowPage />} />
                <Route path="/moderation" element={<ModerationQueuePage />} />
                <Route path="/lora" element={<LoRAListPage />} />
                <Route path="/backends" element={<BackendsListPage />} />

                <Route path="/skus" element={<SKUsListPage />} />
                <Route path="/cardkeys" element={<CardKeysListPage />} />
                <Route path="/catalog" element={<CatalogListPage />} />
                <Route path="/mall" element={<MallListPage />} />
                <Route path="/mall/:id" element={<MallDetailPage />} />
                <Route path="/membership" element={<MembershipPlansPage />} />

                <Route path="/orders" element={<OrdersListPage />} />
                <Route path="/orders/:id" element={<OrderDetailPage />} />
                <Route path="/benefit-orders" element={<BenefitOrdersPage />} />
                <Route path="/payments" element={<PaymentChannelsPage />} />
                <Route path="/fulfillments" element={<FulfillmentsListPage />} />

                <Route path="/tasks" element={<TasksListPage />} />
                <Route path="/tasks/:id" element={<TaskDetailPage />} />

                <Route path="/users" element={<UsersListPage />} />
                <Route path="/users/:id" element={<UserDetailPage />} />

                <Route path="/diamonds" element={<DiamondsListPage />} />

                <Route path="/coupons" element={<CouponsListPage />} />

                <Route path="/ad-accounts" element={<AdAccountsListPage />} />

                <Route path="/attribution" element={<AttributionEventsPage />} />

                <Route path="/integrations" element={<IntegrationsPage />} />

                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </AntApp>
    </ConfigProvider>
  );
}
